<template>
  <VueFinalModal overlay-transition="vfm-fade" content-transition="vfm-fade">
    <div class="InvitePlayersOverlay" @click.self="props.onClose">
      <div class="invite-wrapper" :class="{ 'is-mobile': isMobile }">
        <div class="invite-card">
          <div class="invite-scroll">
            <div class="invite-header">
              <GamSimpleOrganizerSearch ref="searchPlayer" v-bind="getSearchInput" v-model="usernameQuery" />
            </div>

            <div class="invite-content">
              <!-- Ephemeral players (search or this-game invitees without a handshake) -->
              <div
                v-for="player in ephemeralPlayers"
                :key="`eph-${player.id}`"
                :class="['player-row', { 'is-locked': isPlayerLocked(player.id) }]"
                @click="handleTogglePlayer(player)"
              >
                <div class="player-avatar">
                  <img
                    v-if="player.profileImageBlobHash"
                    :src="getImageSrc(player.profileImageBlobHash)"
                    class="picture"
                  />
                  <div v-else class="placeholder">
                    <GamIcon :name="GamIconName.PAWN_EMPTY" />
                  </div>
                </div>

                <div class="player-info">
                  <div class="username">@{{ player.username }}</div>
                  <div class="name">{{ player.firstName }} {{ player.lastName }}</div>
                </div>

                <div class="player-check">
                  <GamIcon
                    v-if="isPlayerLocked(player.id)"
                    :name="GamIconName.CHECK"
                    size="18px"
                    class="check-icon locked"
                    is-current
                  />
                  <GamIcon v-else :name="GamIconName.CHECK" size="18px" class="check-icon" is-current />
                </div>
              </div>

              <!-- Persistent players (recent invitees, then handshake partners) -->
              <div
                v-for="player in persistentPlayers"
                :key="`per-${player.id}`"
                :class="['player-row', { 'is-locked': isPlayerLocked(player.id) }]"
                @click="handleTogglePlayer(player)"
              >
                <div class="player-avatar">
                  <img
                    v-if="player.profileImageBlobHash"
                    :src="getImageSrc(player.profileImageBlobHash)"
                    class="picture"
                  />
                  <div v-else class="placeholder">
                    <GamIcon :name="GamIconName.PAWN_EMPTY" />
                  </div>
                </div>

                <div class="player-info">
                  <div class="username">@{{ player.username }}</div>
                  <div class="name">{{ player.firstName }} {{ player.lastName }}</div>
                </div>

                <div class="player-check">
                  <GamIcon
                    v-if="isPlayerLocked(player.id)"
                    :name="GamIconName.CHECK"
                    size="18px"
                    class="check-icon locked"
                    is-current
                  />
                  <GamIcon
                    v-else-if="selectedIds.has(player.id)"
                    :name="GamIconName.CHECK"
                    size="18px"
                    class="check-icon"
                    is-current
                  />
                  <div v-else class="check-empty" />
                </div>
              </div>
            </div>
          </div>

          <div class="invite-bottom-actions">
            <div :class="['invite-close', { 'is-confirm': filteredPlayerIds.length > 0 }]" @click="handleConfirm()">
              <GamIcon
                :name="filteredPlayerIds.length ? GamIconName.CHECK : GamIconName.X_CLOSE"
                :size="GamIconSize.X_LARGE"
                is-current
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  </VueFinalModal>
</template>

<script setup lang="ts">
import { GambitInvitationType } from '@/core/data/gambit/gambit.type';
import type { BaseUserDto } from '@/core/data/user/user.interface';
import { gambits, localize } from '@/core/gambits';
import { isMobile } from '@/core/helpers/ui.helper';
import type { Result } from '@/core/network/http/httpClient.interface';
import { useAuthStore } from '@/stores/auth.store';
import { useTableStore } from '@/stores/table.store';
import GamIcon from '@/views/components/GamIcon.vue';
import GamSimpleOrganizerSearch from '@/views/components/GamSimpleOrganizerSearch.vue';
import { GamButtonMessageVariant } from '@/views/composables/constants/components/gamButton.constants';
import { GamDropdownCategory } from '@/views/composables/constants/components/gamDropdown.constants';
import { GamIconName, GamIconSize } from '@/views/composables/constants/components/gamIcon.constants';
import { GamInputName } from '@/views/composables/constants/components/gamInput.constants';
import { GamListId } from '@/views/composables/constants/components/gamIntersect.constants';
import type { GamButtonMessageType } from '@/views/composables/models/components/GamButton';
import type { GamSimpleDropdownType } from '@/views/composables/models/components/GamDropdown';
import type { GamSimpleInputSearchType } from '@/views/composables/models/components/GamInput';
import { openProfileLock, ProfileLockReason, ProfileUnlock } from '@/views/main/players/profile-access';
import { storeToRefs } from 'pinia';
import { computed, onMounted, ref } from 'vue';
import { VueFinalModal } from 'vue-final-modal';
import { useToast } from 'vue-toastification';

export type InviteDrawerMode = 'select' | 'join-and-invite' | 'send';

export interface ChosenPlayer {
  id: string;
  username: string;
  firstName: string;
  lastName: string;
  rating: number;
  titles?: string[];
  profileImageBlobHash: string | null;
}

interface InvitePlayersProps {
  gambitId?: string;
  proposalId?: string;
  invitationTarget?: 'gambit' | 'spark';
  participantIds: string[];
  initialPlayers?: ChosenPlayer[];
  onClose: () => void;
  mode?: InviteDrawerMode;
  onSelect?: (players: ChosenPlayer[]) => void;
  onJoinAndInvite?: (playerIds: string[]) => void;
}

const props = withDefaults(defineProps<InvitePlayersProps>(), {
  mode: 'send',
  gambitId: undefined,
  proposalId: undefined,
  invitationTarget: undefined,
  initialPlayers: () => [],
  onSelect: undefined,
  onJoinAndInvite: undefined,
});

const toast = useToast();
const authStore = useAuthStore();
const { user } = storeToRefs(authStore);

const isSparkInvitation = computed(() => props.invitationTarget === 'spark');
const canInviteAnyone = computed(() => authStore.isAdmin());

// Persistent players: handshake partners, with recent invitees pinned first
const persistentPlayersList = ref<ChosenPlayer[]>([]);
// Ephemeral players: search or this-game invitees without a handshake
const ephemeralPlayersList = ref<ChosenPlayer[]>([]);
const selectedIds = ref<Set<string>>(new Set());
const persistentIds = ref<Set<string>>(new Set());
const handshakePartnerIds = ref<Set<string>>(new Set());

function toChosenPlayer(player: BaseUserDto | ChosenPlayer): ChosenPlayer {
  return {
    id: player.id,
    username: player.username,
    firstName: player.firstName || '',
    lastName: player.lastName || '',
    rating: player.rating || 0,
    profileImageBlobHash: player.profileImageBlobHash ?? null,
  };
}

function canInvitePlayer(playerId: string): boolean {
  return canInviteAnyone.value || handshakePartnerIds.value.has(playerId);
}

function getImageSrc(hash: string): string {
  return gambits.imageService.getImageSrc(hash, 100);
}

function isPlayerLocked(playerId: string): boolean {
  return props.participantIds.includes(playerId);
}

// Players from search that are not in the persistent list
const ephemeralPlayers = computed(() => ephemeralPlayersList.value);

const persistentPlayers = computed(() => persistentPlayersList.value);

const filteredPlayerIds = computed(() => {
  const ids: string[] = [];
  for (const id of selectedIds.value) {
    if (!props.participantIds.includes(id)) {
      ids.push(id);
    }
  }
  return ids;
});

function handleTogglePlayer(player: ChosenPlayer) {
  if (isPlayerLocked(player.id)) return;

  if (persistentIds.value.has(player.id)) {
    // Persistent player: toggle selection state, keep in list
    if (selectedIds.value.has(player.id)) {
      selectedIds.value.delete(player.id);
      selectedIds.value = new Set(selectedIds.value);
    } else {
      selectedIds.value.add(player.id);
      selectedIds.value = new Set(selectedIds.value);
    }
  } else {
    // Ephemeral player: remove from list and deselect
    selectedIds.value.delete(player.id);
    selectedIds.value = new Set(selectedIds.value);
    ephemeralPlayersList.value = ephemeralPlayersList.value.filter((p) => p.id !== player.id);
  }
}

function getSelectedPlayers(): ChosenPlayer[] {
  const all = [...ephemeralPlayersList.value, ...persistentPlayersList.value];
  return all.filter((p) => selectedIds.value.has(p.id));
}

async function handleConfirm() {
  if (props.mode === 'select') {
    props.onSelect?.(getSelectedPlayers());
    props.onClose();
    return;
  }

  if (props.mode === 'join-and-invite') {
    if (!filteredPlayerIds.value.length) {
      props.onClose();
      return;
    }
    props.onJoinAndInvite?.(filteredPlayerIds.value);
    props.onClose();
    return;
  }

  if (!filteredPlayerIds.value.length) {
    props.onClose();
    return;
  }

  if (isSparkInvitation.value && !props.proposalId) {
    toast.error(localize('invite.players.error.missingProposal'));
    return;
  }

  if (!isSparkInvitation.value && !props.gambitId) {
    toast.error(localize('invite.players.error.missingGambit'));
    return;
  }

  const result = isSparkInvitation.value
    ? await gambits.sparkService.createInvitations(props.proposalId!, filteredPlayerIds.value)
    : await gambits.gambitService.createInvitations(
        props.gambitId!,
        filteredPlayerIds.value,
        GambitInvitationType.REQUEST,
      );

  if (result.error) {
    toast.error(result.error.message);
    return;
  }

  toast.info(
    filteredPlayerIds.value.length > 1
      ? localize('invite.players.success.many')
      : localize('invite.players.success.one'),
  );
  props.onClose();
}

onMounted(async () => {
  const userId = user.value?.id;

  const [handshakeResult, recentResult] = await Promise.all([
    userId ? gambits.userService.getHandshakePartners(userId) : Promise.resolve({ data: [] as BaseUserDto[] }),
    gambits.userService.getRecentInvitees(),
  ]);

  const handshakePartners = (handshakeResult.data ?? []).map(toChosenPlayer);
  handshakePartnerIds.value = new Set(handshakePartners.map((player) => player.id));

  const recentInvitees = (recentResult.data ?? []).filter((player) => canInvitePlayer(player.id)).map(toChosenPlayer);

  const seen = new Set<string>();
  const prelist: ChosenPlayer[] = [];

  const pushPersistent = (player: ChosenPlayer) => {
    if (seen.has(player.id)) return;
    seen.add(player.id);
    prelist.push(player);
  };

  // This-game outgoing invites are oldest-first; reverse so the newest stay on top.
  for (const player of [...props.initialPlayers].reverse()) {
    selectedIds.value.add(player.id);

    if (canInvitePlayer(player.id)) {
      pushPersistent(toChosenPlayer(player));
      continue;
    }

    ephemeralPlayersList.value.push(toChosenPlayer(player));
  }

  for (const player of recentInvitees) {
    pushPersistent(player);
  }

  for (const player of handshakePartners) {
    pushPersistent(player);
  }

  persistentPlayersList.value = prelist;
  persistentIds.value = seen;
  selectedIds.value = new Set(selectedIds.value);
});

// Search

const formError = ref<GamButtonMessageType | null>(null);
const isLoading = ref<boolean>(false);

const searchPlayer = ref<typeof GamSimpleOrganizerSearch>();
const usernameQuery = ref<string>('');
const listId = GamListId.PLAYERS;

const tableStore = useTableStore<BaseUserDto>(listId)();

const handleClearSearch = () => {
  if (!searchPlayer.value) return;

  usernameQuery.value = '';
  searchPlayer.value.clearInput();
  searchPlayer.value.blurInput();
};

const getSearchInput = computed(
  (): GamSimpleInputSearchType<BaseUserDto, BaseUserDto, GamSimpleDropdownType<BaseUserDto>> => {
    return {
      id: listId,
      onClickClear: handleClearSearch,
      input: {
        canClear: true,
        name: GamInputName.GAM_USERNAME,
        placeholder: localize('invite.players.search.placeholder'),
        leftIcon: GamIconName.PAWN_EMPTY,
        value: usernameQuery.value,
      },
      dropdown: {
        category: GamDropdownCategory.PLAYERS,
        query: usernameQuery.value,
        items: [],
        infiniteLoad: () => {},
      },
      isLoading: isLoading.value,
      onQueryChanged: async (query: string | undefined) => {
        const rawQuery = query ? query.toString() : '';
        const sanitizedQuery = rawQuery.startsWith('@') ? rawQuery.slice(1) : rawQuery;
        const search = sanitizedQuery.length ? sanitizedQuery : undefined;

        await tableStore.setSearchQueryImmediate({ search, preventLoad: true });
      },
      loadCallback: async (): Promise<Result<BaseUserDto[]>> => {
        isLoading.value = true;
        formError.value = null;

        const response = await gambits.userService.getUsers(listId);

        isLoading.value = false;

        if (response.error) {
          formError.value = {
            label: response.error.message,
            variant: GamButtonMessageVariant.ERROR,
          };
        }

        return response;
      },
      selectCallback: async (result: BaseUserDto | null) => {
        if (!result) {
          searchPlayer.value?.closeDropdown();
          searchPlayer.value?.clearInput();
          return;
        }

        if (selectedIds.value.has(result.id)) {
          searchPlayer.value?.closeDropdown();
          searchPlayer.value?.clearInput();
          return;
        }

        if (!canInvitePlayer(result.id)) {
          openProfileLock(result.username, [ProfileUnlock.Handshake], ProfileLockReason.Invite);
          searchPlayer.value?.closeDropdown();
          searchPlayer.value?.clearInput();
          return;
        }

        selectedIds.value.add(result.id);
        selectedIds.value = new Set(selectedIds.value);

        if (persistentIds.value.has(result.id)) {
          const idx = persistentPlayersList.value.findIndex((p) => p.id === result.id);
          if (idx > 0) {
            const [player] = persistentPlayersList.value.splice(idx, 1);
            persistentPlayersList.value.unshift(player);
          }
        } else if (!ephemeralPlayersList.value.find((p) => p.id === result.id)) {
          ephemeralPlayersList.value.unshift(toChosenPlayer(result));
        }

        searchPlayer.value?.closeDropdown();
        searchPlayer.value?.clearInput();
      },
    };
  },
);
</script>

<style lang="scss" scoped>
@use '@/ui/css/mixins' as *;
.InvitePlayersOverlay {
  position: fixed;
  inset: 0;
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
}

.invite-wrapper {
  position: relative;
  width: 380px;
  max-width: calc(100vw - 2rem);
  height: min(700px, calc(100vh - 4rem));
  display: flex;
  flex-direction: column;

  &.is-mobile {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    width: 100%;
    max-width: 100%;
    height: 85vh;

    .invite-card {
      border-radius: 1.5rem 1.5rem 0 0;
    }
  }
}

.invite-card {
  position: relative;
  flex: 1;
  min-height: 0;
  border-radius: 1.5rem;
  border: 1px solid var(--color-white-10);
  background: var(--color-dark-700);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.invite-scroll {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
}

.invite-header {
  position: sticky;
  top: 0;
  padding: 1.25rem;
  background: var(--color-dark-700);
  z-index: 2;
}

.invite-content {
  padding: 0.5rem 1.25rem;
  padding-bottom: 6rem;
}

.player-row {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.625rem 0;
  cursor: pointer;
  transition: opacity var(--transition);
  @include hover-capable {
    &:hover:not(.is-locked) {
      opacity: 0.7;
    }
  }

  &.is-locked {
    cursor: default;
    opacity: 0.5;
  }
}

.player-avatar {
  width: 44px;
  height: 44px;
  flex-shrink: 0;
  border-radius: 50%;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--color-dark-500);

  .picture {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }

  .placeholder {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: var(--color-dark-500);
  }
}

.player-info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
}

.username {
  font-size: 14px;
  font-weight: 500;
  color: var(--color-white-100);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.name {
  font-size: 12px;
  font-weight: 300;
  color: var(--color-white-40);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.player-check {
  flex-shrink: 0;
  width: 2rem;
  height: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;

  .check-icon {
    color: var(--color-pink);
    transition: color var(--transition);
  }

  .check-icon.locked {
    color: var(--color-white-20);
  }

  .check-empty {
    width: 18px;
    height: 18px;
    border-radius: 4px;
    border: 2px solid var(--color-white-20);
    transition: border-color var(--transition);
  }
}

.invite-bottom-actions {
  position: absolute;
  bottom: var(--safe-bottom-offset);
  left: 0;
  right: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 3;
  pointer-events: none;

  > * {
    pointer-events: auto;
  }
}

.invite-close {
  width: 3.5rem;
  height: 3.5rem;
  border-radius: 3.5rem;
  border: 1px solid var(--color-white-10);
  backdrop-filter: blur(8px);
  background-color: var(--color-dark-5);
  color: var(--color-white-70);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 200ms linear;

  @include hover {
    background-color: var(--color-dark-20);
    border-color: var(--color-white-30);
    color: var(--color-white);
  }

  &.is-confirm {
    background-color: var(--color-pink);
    color: var(--color-white);

    @include hover {
      filter: brightness(1.1);
    }
  }

  @include press;
}
</style>
